/**
 * Stable window name for reuse/focus.
 * Use a distinct name so a prior "_blank" tab is not reused as a tab.
 */
export const TPA_PORTAL_POPUP_NAME = "gseven_tpa_portal_popup_v2";

export type OpenTpaPortalPopupResult = {
  popup: Window | null;
  blocked: boolean;
};

/**
 * Opens the insurer portal in a real popup window (not a browser tab when the
 * browser honors `popup=yes` + explicit size). Must run from a user click.
 *
 * Note: Chrome/Edge users can still force "open as tab" via browser settings;
 * we cannot override that OS/browser preference from the web app.
 */
export function openTpaPortalPopup(portalUrl: string): OpenTpaPortalPopupResult {
  if (!portalUrl) {
    return { popup: null, blocked: false };
  }

  const availW = window.screen.availWidth || window.innerWidth;
  const availH = window.screen.availHeight || window.innerHeight;

  // Keep clearly smaller than the screen so Chromium treats it as a popup window.
  const width = Math.min(1100, Math.max(720, Math.floor(availW * 0.48)));
  const height = Math.min(800, Math.max(560, Math.floor(availH * 0.78)));

  const left = Math.min(
    Math.max(0, (window.screenX || 0) + (window.outerWidth || 0)),
    Math.max(0, availW - width)
  );
  const top = Math.max(0, (window.screenY || 0) + 40);

  // `popup=yes` is the modern hint (Chrome 100+) to request a window, not a tab.
  const features = [
    "popup=yes",
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    "resizable=yes",
    "scrollbars=yes",
    "status=yes",
    "toolbar=no",
    "menubar=no",
    "location=yes",
  ].join(",");

  // Open blank first so Chromium classifies it as a sized popup, then navigate.
  // Do not pass noopener — we need the Window handle to focus/reuse.
  const popup = window.open("about:blank", TPA_PORTAL_POPUP_NAME, features);

  if (!popup || popup.closed) {
    return { popup: null, blocked: true };
  }

  try {
    popup.location.replace(portalUrl);
  } catch {
    try {
      popup.location.href = portalUrl;
    } catch {
      // If navigation fails, still return the window reference.
    }
  }

  try {
    popup.focus();
  } catch {
    // ignore
  }

  return { popup, blocked: false };
}

export function focusTpaPortalPopup(popup: Window | null): boolean {
  if (!popup || popup.closed) return false;
  try {
    popup.focus();
    return true;
  } catch {
    return false;
  }
}
